import { useState, useCallback, useEffect, useRef, lazy, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  trackQuoteReady,
  trackQuoteError,
  trackMaterialSelect,
  trackColorSelect,
} from '@/services/analytics';
import { FileUploader } from '@/components/upload/FileUploader';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

const ModelViewer = lazy(() =>
  import('@/components/upload/ModelViewer').then((m) => ({ default: m.ModelViewer })),
);
import { QuoteResult } from '@/components/upload/QuoteResult';
import { ColorSwatch } from '@/components/quote/ColorSwatch';
import { CheckoutPanel } from '@/components/quote/CheckoutPanel';
import { useFileUpload } from '@/hooks/useFileUpload';
import { useQuote } from '@/hooks/useQuote';
import { useModelVolume } from '@/hooks/useModelVolume';
import { useMaterials } from '@/hooks/useMaterials';
import { quotesApi } from '@/services/quotes';
import { Button } from '@/components/ui/Button';

export const QuotePage = () => {
  const [searchParams] = useSearchParams();
  const existingQuoteId = searchParams.get('id');
  const reorderQuoteId = searchParams.get('reorder');
  const reorderMaterial = searchParams.get('material');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedMaterialId, setSelectedMaterialId] = useState<string | null>(reorderMaterial);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const { isUploading, progress, error, quoteId: uploadedQuoteId, upload, reset } = useFileUpload();
  const activeQuoteId = existingQuoteId ?? reorderQuoteId ?? uploadedQuoteId;
  const { data: quote, isLoading: quoteLoading } = useQuote(activeQuoteId);
  const { estimate, calculate, reset: resetVolume } = useModelVolume();
  const { data: materials } = useMaterials();

  // Fetch presigned download URL for existing quotes with 3MF files
  const is3mfQuote = quote?.fileName.toLowerCase().endsWith('.3mf');
  const previewQuoteId = existingQuoteId ?? reorderQuoteId;
  const canPreview =
    previewQuoteId &&
    !selectedFile &&
    is3mfQuote &&
    (quote?.status === 'processing' || quote?.status === 'ready' || quote?.status === 'ordered');

  const { data: downloadUrl } = useQuery({
    queryKey: ['quote-download-url', previewQuoteId],
    queryFn: () => quotesApi.getDownloadUrl(previewQuoteId!),
    enabled: !!canPreview,
    staleTime: 10 * 60 * 1000, // 10 min (URL expires in 15)
  });

  const handleFileSelected = useCallback(
    async (file: File) => {
      setSelectedFile(file);
      calculate(file);
      await upload(file);
    },
    [upload, calculate],
  );

  const handleSelectMaterial = useCallback((materialId: string) => {
    setSelectedMaterialId(materialId);
    setSelectedColor(null); // Reset color when material changes
    const mat = materials?.find((m) => m.materialId === materialId);
    trackMaterialSelect({ material_id: materialId, material_name: mat?.name ?? materialId });
  }, [materials]);

  const handleReset = useCallback(() => {
    setSelectedFile(null);
    setSelectedMaterialId(null);
    setSelectedColor(null);
    reset();
    resetVolume();
  }, [reset, resetVolume]);

  const selectedPrice = quote?.prices.find(
    (p) => p.materialId === selectedMaterialId,
  );

  // Track when a quote finishes processing (fires once per quote)
  const trackedQuoteId = useRef<string | null>(null);
  useEffect(() => {
    if (!quote || !activeQuoteId || trackedQuoteId.current === activeQuoteId) return;
    if (quote.status === 'ready') {
      trackedQuoteId.current = activeQuoteId;
      trackQuoteReady({
        quote_id: activeQuoteId,
        volume_cm3: quote.volumeCm3,
        material_count: quote.prices.length,
      });
    } else if (quote.status === 'rejected' || quote.status === 'expired') {
      trackedQuoteId.current = activeQuoteId;
      trackQuoteError({ quote_id: activeQuoteId, error_type: quote.status });
    }
  }, [quote, activeQuoteId]);

  // Determine if we have content to show (either a local file or an existing/reorder quote)
  const hasContent = selectedFile || existingQuoteId || reorderQuoteId;

  return (
    <div
      className="container-main"
      style={{
        maxWidth: '1100px',
        paddingTop: 'var(--space-12)',
        paddingBottom: 'var(--space-12)',
      }}
    >
      <div style={{ marginBottom: 'var(--space-8)' }}>
        <h1
          style={{
            fontSize: 'var(--text-3xl)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--text-primary)',
            letterSpacing: 'var(--tracking-tight)',
          }}
        >
          {existingQuoteId ? 'Quote Details' : reorderQuoteId ? 'Reorder Print' : 'Get an Instant Quote'}
        </h1>
        <p
          style={{
            marginTop: 'var(--space-2)',
            color: 'var(--text-secondary)',
          }}
        >
          {existingQuoteId
            ? 'Review your quote and select a material to proceed.'
            : reorderQuoteId
              ? 'Select your material and proceed to checkout.'
              : 'Upload your 3D model file and get pricing for all available materials instantly.'}
        </p>
      </div>

      {/* Show uploader only when there is no file and no existing quote */}
      {!hasContent && (
        <FileUploader
          onFileSelected={handleFileSelected}
          isUploading={isUploading}
          progress={progress}
        />
      )}

      {error && (
        <div
          style={{
            marginTop: 'var(--space-4)',
            borderRadius: 'var(--radius-lg)',
            backgroundColor: 'var(--bg-error-subtle)',
            padding: 'var(--space-4)',
            fontSize: 'var(--text-sm)',
            color: 'var(--color-error-700)',
          }}
        >
          {error}
          <button
            onClick={handleReset}
            style={{
              marginLeft: 'var(--space-2)',
              textDecoration: 'underline',
              background: 'none',
              border: 'none',
              color: 'inherit',
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        </div>
      )}

      {hasContent && (
        <div className="quote-layout">
          {/* Left column -- configuration */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-8)' }}>
            {/* File info bar */}
            <div
              className="flex flex-col sm:flex-row sm:items-center justify-between"
              style={{
                border: '1px solid var(--border-default)',
                backgroundColor: 'var(--bg-surface)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-3) var(--space-4)',
                gap: 'var(--space-3)',
              }}
            >
              <div className="flex items-center" style={{ gap: 'var(--space-3)', minWidth: 0 }}>
                <div
                  className="flex items-center justify-center"
                  style={{
                    height: '2.5rem',
                    width: '2.5rem',
                    borderRadius: 'var(--radius-lg)',
                    backgroundColor: 'var(--bg-accent-subtle)',
                    color: 'var(--text-accent)',
                    flexShrink: 0,
                  }}
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                  </svg>
                </div>
                <div>
                  <p
                    style={{
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-weight-medium)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    {selectedFile?.name ?? quote?.fileName ?? 'Loading...'}
                  </p>
                  <p
                    style={{
                      fontSize: 'var(--text-xs)',
                      color: 'var(--text-tertiary)',
                    }}
                  >
                    {selectedFile
                      ? `${(selectedFile.size / (1024 * 1024)).toFixed(2)} MB`
                      : quote?.volumeCm3
                        ? `${quote.volumeCm3} cm\u00B3`
                        : ''}
                    {estimate && ` | ~${estimate.volumeCm3} cm\u00B3`}
                  </p>
                </div>
              </div>
              {!existingQuoteId && (
                <Button variant="ghost" size="sm" onClick={handleReset}>
                  Upload Different File
                </Button>
              )}
            </div>

            {/* 3D Preview -- from local file or presigned URL */}
            {(selectedFile?.name.toLowerCase().endsWith('.3mf') || downloadUrl) && (
              <div>
                <SectionHeading title="3D Preview" />
                <Suspense fallback={<LoadingSpinner size="lg" />}>
                  <ModelViewer
                    file={selectedFile}
                    url={downloadUrl}
                    color={selectedColor}
                    className="h-[400px]"
                  />
                </Suspense>
              </div>
            )}

            {/* Client-side estimate while server processes */}
            {estimate && !quote && !quoteLoading && (
              <div
                style={{
                  borderRadius: 'var(--radius-lg)',
                  border: `1px solid ${estimate.exceedsBuildVolume ? 'var(--color-error-300)' : 'var(--border-default)'}`,
                  backgroundColor: estimate.exceedsBuildVolume ? 'var(--color-error-50)' : 'var(--bg-surface-sunken)',
                  padding: 'var(--space-4)',
                }}
              >
                {estimate.exceedsBuildVolume && (
                  <p
                    style={{
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--color-error-700)',
                      marginBottom: 'var(--space-2)',
                    }}
                  >
                    This model exceeds the maximum build volume of 25.6 x 25.6 x 25.6 cm.
                  </p>
                )}
                <p style={{ fontSize: 'var(--text-sm)', color: estimate.exceedsBuildVolume ? 'var(--color-error-600)' : 'var(--text-secondary)' }}>
                  Estimated volume: <span style={{ fontWeight: 'var(--font-weight-semibold)' }}>{estimate.volumeCm3} cm{'\u00B3'}</span>
                  {' | '}
                  Bounding box: {estimate.boundingBox.width} x {estimate.boundingBox.height} x{' '}
                  {estimate.boundingBox.depth} cm
                </p>
                {!estimate.exceedsBuildVolume && (
                  <p
                    style={{
                      marginTop: 'var(--space-1)',
                      fontSize: 'var(--text-xs)',
                      color: 'var(--text-tertiary)',
                    }}
                  >
                    Precise pricing will appear once server analysis completes.
                  </p>
                )}
              </div>
            )}

            {/* Material selection */}
            {(activeQuoteId || quoteLoading) && (
              <QuoteResult
                quote={quote}
                isLoading={quoteLoading && !quote}
                selectedMaterialId={selectedMaterialId}
                onSelectMaterial={handleSelectMaterial}
                materials={materials}
              />
            )}

            {/* Color selection -- shown after material is selected */}
            {selectedMaterialId && materials && (
              <div>
                <SectionHeading title="Choose Your Color" />
                <ColorSwatch
                  materials={materials}
                  selectedMaterialId={selectedMaterialId}
                  selectedColor={selectedColor}
                  onSelectColor={(c) => {
                    setSelectedColor(c);
                    if (selectedMaterialId) {
                      trackColorSelect({ material_id: selectedMaterialId, color: c });
                    }
                  }}
                />
              </div>
            )}
          </div>

          {/* Right column -- sticky checkout panel */}
          {(activeQuoteId || quoteLoading) && (
            <div className="quote-sidebar">
              <div className="quote-sidebar-sticky">
                <CheckoutPanel
                  fileName={selectedFile?.name ?? quote?.fileName ?? ''}
                  volumeCm3={quote?.volumeCm3 ?? null}
                  boundingBox={quote?.boundingBox ?? estimate?.boundingBox ?? null}
                  selectedMaterialId={selectedMaterialId}
                  selectedMaterialName={
                    selectedMaterialId
                      ? (materials?.find((m) => m.materialId === selectedMaterialId)?.name ?? selectedMaterialId)
                      : null
                  }
                  selectedColor={selectedColor}
                  price={selectedPrice?.price ?? null}
                  rawPrice={selectedPrice?.rawPrice ?? null}
                  estimatedDays={selectedPrice?.estimatedDays ?? null}
                  quoteId={activeQuoteId ?? null}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// =============================================
// Section heading
// =============================================

const SectionHeading = ({ title }: { title: string }) => (
  <h3
    style={{
      marginBottom: 'var(--space-4)',
      fontSize: 'var(--text-lg)',
      fontWeight: 'var(--font-weight-semibold)',
      color: 'var(--text-primary)',
    }}
  >
    {title}
  </h3>
);
